import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { types as coreTypes } from '@sitebuilder/core';
import { hooks as commonHooks } from '@sitebuilder/common';
import { StateShape as ReduxStateShape } from '../reducers';
import { actionCreators } from '../reducers/components';
import AddNewItemForm, { DataKeys } from '../components/BaseComponents/AddNewItemForm';
import { formIDRegex, formNameRegex } from '../utils';

const idRegex = formIDRegex;
const nameRegex = formNameRegex;

type FormData = coreTypes.SaveComponentFormData;

interface SaveComponentFormProps {
    formTitle?: string;
    addNewComponent?: boolean;
    defaultActiveAccordionKey?: string;
    urlState?: string;
}

const SaveComponentForm = (props: SaveComponentFormProps) => {
    const { formTitle, addNewComponent, defaultActiveAccordionKey, urlState } = props;
    const dispatch = useDispatch();

    // initial form data
    const initialData = {
        id: '',
        name: '',
        description: '',
    };
    const [data, setData] = React.useState<FormData>(initialData);
    // error messages for invalid inputs
    const [dataError, setDataError] = React.useState<FormData>(initialData);

    // component save error
    const [error, setError] = React.useState<string>('');

    const [success, setSuccess] = React.useState<string>('');

    const {
        componentLoading,
        componentSaveLoading,
        componentSaveSucess,
        componentSaveError,
        selectedWidgetId,
    } = useSelector((state: ReduxStateShape) => ({
        ...state?.components,
        selectedWidgetId: state.templateEditor?.selectedId,
    }));

    const i18n = commonHooks.useLocalization();

    useEffect(() => {
        setData(initialData);
        setDataError(initialData);
        setError('');
        setSuccess('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWidgetId]);

    useEffect(() => {
        if (componentSaveSucess) {
            setSuccess(componentSaveSucess);
            // TODO: libraryName(public) shouldn't be hardcoded
            dispatch(actionCreators.listComponents('public', urlState || ''));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentSaveSucess]);

    useEffect(() => {
        if (componentSaveError) {
            setError(i18n.t('component_form_save_error'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentSaveError]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: DataKeys) => {
        const { value } = e?.target;

        if (error) {
            setError('');
        }

        if (success) {
            setSuccess('');
        }

        if (!value && dataError[field]) {
            setDataError({ ...dataError, [field]: '' });
        }

        if (field === 'id') {
            if (value) {
                if (!idRegex.test(value)) {
                    setDataError({ ...dataError, id: i18n.t('form_invalid_id') });
                } else if (value.length > 32) {
                    setDataError({ ...dataError, id: i18n.t('form_invalid_id_length') });
                } else setDataError({ ...dataError, id: '' });
            } else {
                setDataError({ ...dataError, id: i18n.t('form_missing_id') });
            }
        }

        if (field === 'name') {
            if (value) {
                if (nameRegex.test(value))
                    setDataError({ ...dataError, name: i18n.t('form_invalid_name') });
                else if (value.length > 64)
                    setDataError({ ...dataError, name: i18n.t('form_invalid_name_length') });
                else setDataError({ ...dataError, name: '' });
            } else {
                setDataError({ ...dataError, name: i18n.t('form_missing_name') });
            }
        }

        if (field === 'description') {
            if (value && value.length > 256)
                setDataError({
                    ...dataError,
                    name: i18n.t('form_invalid_description_length'),
                });
            else setDataError({ ...dataError, description: '' });
        }

        setData({
            ...data,
            [field]: value,
        });
    };

    const validateFormData = () => {
        let valid = false;
        const { id, name } = data;
        const err: FormData = {
            id: '',
            name: '',
            description: '',
        };

        if (!id) {
            err.id = i18n.t('form_missing_id');
        }

        if (!name) {
            err.name = i18n.t('form_missing_name');
        }

        if (!_.values(err).every(_.isEmpty)) setDataError({ ...dataError, ...err });

        valid =
            _.every(err, (e) => e === '' || _.isNull(e)) &&
            _.every(dataError, (e) => e === '' || _.isNull(e));
        return valid;
    };

    const handleSubmit = async () => {
        const isFormValid = validateFormData();

        if (isFormValid) {
            // save the component
            if (addNewComponent) {
                await dispatch(
                    actionCreators.saveComponent('public', data, '', addNewComponent, urlState)
                );
            } else {
                await dispatch(
                    actionCreators.saveComponent(
                        'public',
                        data,
                        selectedWidgetId as any,
                        false,
                        urlState
                    )
                );
            }

            setData(initialData);
            setDataError(initialData);
        }
    };

    return (
        <AddNewItemForm
            formTitle={formTitle || 'Save Component to Gallery'}
            data={data}
            dataError={dataError}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            showDescription={true}
            saveError={error}
            saveSuccess={success}
            showSaveLoading={componentLoading || componentSaveLoading}
            defaultActiveAccordionKey={defaultActiveAccordionKey || ''}
        />
    );
};

export default SaveComponentForm;
